import { fetchWrapper } from './fetchWrapper'
import { extractApiErrorMessage } from './shared'

export interface PresignedUrlResponse {
  url: string
  fields: Record<string, string>
  file_path: string
}

export interface UploadOptions {
  onProgress?: (progress: number) => void
  onComplete?: (path: string, virusScanStatus?: string) => void
  onError?: (error: string, virusScanStatus?: string) => void
  onVirusScanStatus?: (status: string, message?: string) => void
}

/**
 * Get presigned URL from backend
 */
export async function getPresignedUrl(
  endpoint: string,
  filename: string,
  fileSize: number
): Promise<PresignedUrlResponse> {
  const response = await fetchWrapper(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ filename, file_size: fileSize })
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(extractApiErrorMessage(errorData, 'Failed to get presigned URL'))
  }

  return await response.json()
}

/**
 * Upload file directly to S3 using presigned URL with real progress tracking
 */
export async function uploadToS3WithProgress(
  file: File,
  presignedUrl: PresignedUrlResponse,
  onProgress: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()

    // Add presigned fields first
    Object.keys(presignedUrl.fields).forEach(key => {
      formData.append(key, presignedUrl.fields[key])
    })

    // Add file last (required by S3)
    formData.append('file', file)

    // Track upload progress
    xhr.upload.addEventListener('progress', e => {
      if (e.lengthComputable) {
        const progress = (e.loaded / e.total) * 100
        onProgress(progress)
      }
    })

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`S3 upload failed with status: ${xhr.status}`))
      }
    })

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    // Send request
    xhr.open('POST', presignedUrl.url)
    xhr.send(formData)
  })
}

/**
 * Notify backend of upload completion and stream status
 */
export async function notifyUploadComplete(
  endpoint: string,
  filePath: string,
  options: UploadOptions
): Promise<void> {
  const {
    onProgress = () => {},
    onComplete = () => {},
    onError = () => {},
    onVirusScanStatus = () => {}
  } = options

  try {
    const response = await fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ file_path: filePath }),
      timeoutMs: 120000
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Response body is not readable')
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const chunk = new TextDecoder().decode(value)
      const lines = chunk.split('\n').filter(line => line.trim() !== '')

      for (const line of lines) {
        const data = JSON.parse(line)

        if (data.error) {
          onError(data.error, data.virus_scan_status)
          return
        }

        if (data.progress) {
          onProgress(data.progress)
        }

        if (data.virus_scan_status) {
          onVirusScanStatus(data.virus_scan_status, data.message)
        }

        if (data.status === 'completed') {
          onComplete(data.path, data.virus_scan_status)
          return
        }

        if (data.status === 'scan_complete') {
          onVirusScanStatus(data.virus_scan_status, data.message)
          if (
            data.virus_scan_status === 'NO_THREATS_FOUND' ||
            data.virus_scan_status === 'SCAN_SKIPPED'
          ) {
            onComplete(data.path || filePath, data.virus_scan_status)
          }
          return
        }

        if (data.status === 'scanning') {
          onProgress(100)
          if (data.message) {
            let scanMessage = data.message
            if (data.scan_progress !== undefined) {
              const scanPercent = Math.round(data.scan_progress)
              scanMessage = `${data.message} • ${scanPercent}% scanned`
            }
            onVirusScanStatus('PENDING', scanMessage)
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : 'An unknown error occurred')
  }
}

/**
 * Pure presigned URL upload to S3 - just uploads, no backend communication
 */
export async function uploadWithPresignedUrl(
  file: File,
  presignedUrl: PresignedUrlResponse,
  onProgress?: (progress: number) => void
): Promise<void> {
  await uploadToS3WithProgress(file, presignedUrl, onProgress || (() => {}))
}
